import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);

// Define the valid event types
export type EventType = 'workshop' | 'social' | 'hackathon' | 'meeting' | 'career';

export interface Event {
  id: string;
  title: string;
  date: dayjs.Dayjs;
  endDate?: dayjs.Dayjs; // Optional end date for multi-day events
  time: string;
  location: string;
  type: EventType;  // Use the EventType type here
  description: string;
}

// Add a new interface for recurring events
interface RecurringEvent extends Event {
  recurrence?: {
    pattern: 'weekly';
    endDate: dayjs.Dayjs;
  };
}

export const events: RecurringEvent[] = [];


export const getEventTypeColor = (type: EventType) => {  // Update parameter type
  const colors = {
    workshop: '#4d1979',    // Purple
    social: '#00b2a9',      // Teal
    hackathon: '#fa4616',   // Orange
    meeting: '#c1d82f',     // Lime
    career: '#2196f3',      // Blue
  };
  return colors[type];
};

// Helper function to check if a date falls within a recurring pattern
const isRecurringEventActive = (event: RecurringEvent, date: dayjs.Dayjs) => {
  if (!event.recurrence) return false;

  const { pattern, endDate } = event.recurrence;
  const startDate = event.date;
  const today = dayjs().startOf('day');

  // Don't show past events
  if (date.isBefore(today)) return false;

  // Check if the date is within the recurrence range
  if (!date.isBetween(startDate, endDate, 'day', '[]')) return false;

  if (pattern === 'weekly') {
    // Check if it's the same day of the week
    return date.day() === event.date.day();
  }

  return false;
};

export const hasEvents = (date: dayjs.Dayjs) => {
  const today = dayjs().startOf('day');
  
  // Don't show past events
  if (date.isBefore(today)) return false;

  return events.some(event => {
    // Check for multi-day events
    if (event.endDate) {
      return date.isBetween(event.date, event.endDate, 'day', '[]') && 
             !date.isBefore(today);
    }
    // Check for recurring events
    if (event.recurrence) {
      return isRecurringEventActive(event, date);
    }
    // Check regular single-day events
    return event.date.isSame(date, 'day') && 
           !date.isBefore(today);
  });
};

export const getEventsForDate = (date: dayjs.Dayjs) => {
  const today = dayjs().startOf('day');
  
  // Don't show past events
  if (date.isBefore(today)) return [];

  return events.filter(event => {
    // Check for multi-day events
    if (event.endDate) {
      return date.isBetween(event.date, event.endDate, 'day', '[]') && 
             !date.isBefore(today);
    }
    // Check for recurring events
    if (event.recurrence) {
      return isRecurringEventActive(event, date);
    }
    // Check regular single-day events
    return event.date.isSame(date, 'day') && 
           !date.isBefore(today);
  });
}; 